import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { isNativeApp } from "@/lib/nativeBridge";
import { useAuth } from "@/contexts/AuthContext";
import AdminModeSwitcher from "@/components/AdminModeSwitcher";
import api from "@/lib/api";
import { HouseIcon as House, DogIcon as Dog, PawPrintIcon as PawPrint, TreeStructureIcon as TreeStructure, UsersFourIcon as UsersFour, UsersThreeIcon as UsersThree, SignOutIcon as SignOut, CurrencyDollarIcon as CurrencyDollar, UserCircleIcon as UserCircle, ReceiptIcon as Receipt, CalendarIcon as Calendar, EnvelopeIcon as Envelope, HeartStraightIcon as HeartStraight, GearIcon as Gear, TagIcon as Tag, ArrowsLeftRightIcon as ArrowsLeftRight, GitForkIcon as GitFork, PaperPlaneTiltIcon as PaperPlaneTilt, ChartBarIcon as ChartBar, ListIcon as MenuIcon, XIcon as CloseIcon, CaretDownIcon as CaretDown, ShieldStarIcon as ShieldStar, ChatCircleIcon as ChatCircle, MagnifyingGlassIcon as MagnifyingGlass, QuestionIcon as Question, ImageIcon as ImageIcon, StorefrontIcon as Storefront, CompassIcon as Compass, MegaphoneIcon as Megaphone, VideoCameraIcon as VideoCamera, SparkleIcon as Sparkle, TruckIcon as Truck, PulseIcon as Pulse, HeartIcon as Heart, BookOpenIcon as BookOpen, GraduationCapIcon as GraduationCap, WrenchIcon as Wrench, TreeIcon as Tree } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { PushPinIcon as PushPin } from "@phosphor-icons/react";
import SearchDialog from "@/components/SearchDialog";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileExploreDrawer from "@/components/MobileExploreDrawer";
import GoogleSheetsInstructionsDialog from "@/components/GoogleSheetsInstructionsDialog";
import NotificationsBell from "@/components/NotificationsBell";
import AppInstallAndPushPrompt from "@/components/AppInstallAndPushPrompt";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import VenueAckBanner from "@/components/VenueAckBanner";
import LocationGate from "@/components/LocationGate";
import Tax1099KBanner from "@/components/Tax1099KBanner";
import useProductTour from "@/hooks/useProductTour";
import BackButton from "@/components/BackButton";
import { track } from "@/lib/analytics";

// User-facing navigation, organized methodically.
//   • "Your Kennel" group  — personal records and workflow that belong
//     to the user's own account.
//   • "Community" group   — public-facing pages for discovering other
//     dogs, breeds, and events.
//
// Every entry carries a `group` so the mobile drawer can render section
// headers; the desktop top nav still renders them as a single ordered
// row (personal items first, then community).
//
// `subgroup` (optional) drives the small inline section dividers inside
// the mobile drawer so 30+ links don't look like a phone book. Items
// without a `subgroup` float to the TOP of their parent group (the
// "most-used / pinned" zone). Within each subgroup, order matters and
// reflects observed frequency from the previous 90 days of analytics.
const NAV_GROUPS = {
  KENNEL: "My kennel",
  COMMUNITY: "Explore",
};

// Subgroup labels — short, all-caps in the UI, ordered top-to-bottom
// the way they appear in the drawer. Keys are stable IDs; values are
// the visible labels.
const KENNEL_SUBGROUPS = {
  DOGS: "Dogs & breeding",
  SELLING: "Selling",
  MONEY: "Money",
  GROW: "Grow your kennel",
};
const COMMUNITY_SUBGROUPS = {
  BROWSE: "Browse",
  CONNECT: "Community",
  PLATFORM: "About the platform",
};

const navItems = [
  // --- My kennel · pinned (most-used daily, no subgroup) -----------
  { to: "/dashboard",            label: "Dashboard",       icon: House,           group: NAV_GROUPS.KENNEL },
  { to: "/calendar",             label: "Calendar",        icon: Calendar,        group: NAV_GROUPS.KENNEL },
  { to: "/inbox",                label: "Inbox",           icon: Envelope,        group: NAV_GROUPS.KENNEL, badge: true },
  { to: "/my-bookings",          label: "My bookings",     icon: Calendar,        group: NAV_GROUPS.KENNEL },
  // --- My kennel · Dogs & breeding ---------------------------------
  { to: "/dogs",                 label: "My Dogs",         icon: Dog,             group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder", "dog_owner"] },
  { to: "/dog-log",              label: "Dog Log",         icon: HeartStraight,   group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder", "dog_owner"] },
  { to: "/litters",              label: "My Litters",      icon: PawPrint,        group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder"] },
  { to: "/litters/upcoming/new", label: "Plan a breeding", icon: Heart,           group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder"] },
  { to: "/pedigree",             label: "Pedigrees",       icon: TreeStructure,   group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder", "dog_owner", "buyer"] },
  { to: "/transfers",            label: "Transfers",       icon: ArrowsLeftRight, group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.DOGS, personas: ["breeder", "dog_owner"] },
  // --- My kennel · Selling -----------------------------------------
  { to: "/me/kennel",            label: "My Kennel page",  icon: UsersFour,       group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["breeder"] },
  { to: "/escrow/my",            label: "Sales & Purchases", icon: ArrowsLeftRight, group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["breeder", "buyer"] },
  { to: "/me/saved-searches",    label: "Saved searches",  icon: MagnifyingGlass, group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["buyer"] },
  { to: "/contracts",            label: "Contracts",       icon: PaperPlaneTilt,  group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["breeder", "buyer"] },
  { to: "/tours",                label: "Video tours",     icon: VideoCamera,     group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["breeder", "buyer"] },
  { to: "/me/store",             label: "My Storefront",   icon: Storefront,      group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.SELLING, personas: ["storefront_seller", "breeder"] },
  // --- My kennel · Money -------------------------------------------
  { to: "/billing",              label: isNativeApp() ? "My Account" : "Billing", icon: isNativeApp() ? UserCircle : CurrencyDollar,  group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.MONEY },
  { to: "/payouts",              label: "Payouts",         icon: Receipt,         group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.MONEY, personas: ["breeder", "transporter", "service_provider", "storefront_seller"] },
  { to: "/breeder/analytics",    label: "Analytics",       icon: ChartBar,        group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.MONEY, personas: ["breeder", "storefront_seller"] },
  // --- My kennel · Grow --------------------------------------------
  { to: "/share-kennel",         label: "Share my kennel", icon: PaperPlaneTilt,  group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.GROW, personas: ["breeder"] },
  { to: "/me/referral",          label: "Refer & earn",    icon: ChatCircle,      group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.GROW },
  { to: "/import/dogs",          label: "Bulk import",     icon: PaperPlaneTilt,  group: NAV_GROUPS.KENNEL, subgroup: KENNEL_SUBGROUPS.GROW, personas: ["breeder"] },

  // --- Explore · pinned (start here, no subgroup) -------------------
  { to: "/shop",                 label: "Shop everything", icon: Storefront,      group: NAV_GROUPS.COMMUNITY },
  { to: "/find-pros",            label: "Find a pro",      icon: GraduationCap,   group: NAV_GROUPS.COMMUNITY },
  { to: "/provider/me",          label: "My Provider page", icon: Wrench,         group: NAV_GROUPS.COMMUNITY, personas: ["service_provider"] },
  { to: "/invite",               label: "Invite friends",  icon: PaperPlaneTilt,  group: NAV_GROUPS.COMMUNITY },
  // --- Explore · Browse --------------------------------------------
  { to: "/marketplace",          label: "Dog Marketplace", icon: Dog,             group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  { to: "/storefronts",          label: "Pet Supplies & Merch", icon: Storefront, group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  { to: "/breeders",             label: "Breeders",        icon: UsersFour,       group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  { to: "/breeds",               label: "Breeds",          icon: Compass,         group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  { to: "/breeding",             label: "Breeding Matchmaker", icon: HeartStraight,   group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  { to: "/events",               label: "Events",          icon: Calendar,        group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.BROWSE },
  // --- Explore · Connect -------------------------------------------
  { to: "/feed",                 label: "The Dog's Voice", icon: ChatCircle,      group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.CONNECT },
  { to: "/messages",             label: "Messages",        icon: ChatCircle,      group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.CONNECT, chatBadge: true },
  { to: "/offers/my",            label: "Offers",          icon: HeartStraight,   group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.CONNECT },
  // --- Explore · About the platform --------------------------------
  { to: "/services",             label: "All services & pricing", icon: Storefront, group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.PLATFORM },
  { to: "/features",             label: "Platform features",      icon: Sparkle,    group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.PLATFORM },
  { to: "/one-pagers",           label: "Persona 1-pagers (PDF)", icon: PaperPlaneTilt, group: NAV_GROUPS.COMMUNITY, subgroup: COMMUNITY_SUBGROUPS.PLATFORM, adminOnly: true },
];

// Ordered subgroup labels per parent group — used by DrawerGroup to
// render subheaders in a stable order. `null` represents the pinned
// (most-used) items at the top of each group.
const KENNEL_SUBGROUP_ORDER = [
  null,
  KENNEL_SUBGROUPS.DOGS,
  KENNEL_SUBGROUPS.SELLING,
  KENNEL_SUBGROUPS.MONEY,
  KENNEL_SUBGROUPS.GROW,
];
const COMMUNITY_SUBGROUP_ORDER = [
  null,
  COMMUNITY_SUBGROUPS.BROWSE,
  COMMUNITY_SUBGROUPS.CONNECT,
  COMMUNITY_SUBGROUPS.PLATFORM,
];

// Admin-only links — collapsed into the "Admin" dropdown on desktop and
// listed individually in the mobile drawer.
//
// Note: Transfer queue and Pedigree additions are intentionally NOT
// listed as their own entries — they live inside the unified
// "Approvals" page so admins have a single inbox for everything that
// needs review. The dedicated `/admin/transfers` and
// `/admin/pedigree-additions` pages still exist and are reachable via
// the "Open full queue" link on the Approvals page.
const adminItems = [
  { to: "/admin/approvals", label: "Approvals", icon: ShieldStar, approvalsBadge: true },
  { to: "/admin/payments", label: "Payments & revenue", icon: Receipt },
  { to: "/admin/growth", label: "Growth dashboard", icon: ChartBar },
  { to: "/admin/share-invite", label: "Share & invite", icon: Megaphone },
  { to: "/admin/data", label: "Data & ops", icon: ChartBar },
  { to: "/admin/master-pedigree", label: "Master pedigree", icon: TreeStructure },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/promo-codes", label: "Promo codes", icon: Tag },
  { to: "/admin/breeds", label: "Breed catalog", icon: Compass },
  { to: "/admin/escrow", label: "Escrow & sales", icon: ArrowsLeftRight },
  { to: "/admin/storefronts", label: "The Dog Shop", icon: Storefront },
  { to: "/admin/transporters", label: "Transporters", icon: Truck },
  { to: "/admin/transport-disputes", label: "Transport disputes", icon: Truck },
  { to: "/admin/marketing", label: "Marketing kit", icon: Megaphone },
  { to: "/admin/feed-prompts", label: "Feed prompts", icon: ChatCircle },
  { to: "/admin/about-photos", label: "About photos", icon: ImageIcon },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [pendingTransfers, setPendingTransfers] = useState(0);
  const [pendingPedigree, setPendingPedigree] = useState(0);
  const [pendingReports, setPendingReports] = useState(0);

  // Native shell: silently re-register the push token on launch when
  // permission was already granted (first-time prompt lives in
  // Settings → "Enable notifications" so it's user-initiated).
  useEffect(() => {
    if (!user || !isNativeApp()) return;
    import("@/lib/native").then(({ registerPushNotifications }) =>
      registerPushNotifications(api, { silent: true })
    ).catch(() => {});
  }, [user?.id]);

  // Refresh unread count: on route change AND every 60s in background.
  // Pauses when the tab is hidden, refreshes immediately on resume.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCount = () => {
      if (document.hidden) return;
      api.get("/messages/unread-count", { silent: true })
        .then((r) => { if (!cancelled) setUnread(r.data.count || 0); })
        .catch(() => {});
      api.get("/chat/unread-count", { silent: true })
        .then((r) => { if (!cancelled) setChatUnread(r.data.unread || 0); })
        .catch(() => {});
      if (user.role === "admin") {
        api.get("/admin/approvals", { silent: true })
          .then((r) => {
            if (cancelled) return;
            const t = r.data?.totals || {};
            setPendingTransfers(t.transfers || 0);
            setPendingPedigree(t.pedigree_additions || 0);
            setPendingReports(t.post_reports || 0);
          })
          .catch(() => {});
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    const onVisible = () => { if (!document.hidden) fetchCount(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, location.pathname]);

  const handleLogout = () => {
    // logout() handles the hard navigation back to the landing page.
    // Don't call navigate("/") here or the in-app router will race with
    // the still-mounted ProtectedRoute on /dashboard and ship the user
    // to /login instead of "/".
    logout();
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Auto-close the mobile drawer on route change so the user can see the new page.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // One-time discoverability hint: the 3rd time a user opens the drawer
  // (and they haven't pinned anything yet), nudge them about the
  // long-press-to-pin gesture. Most users won't find hidden gestures
  // on their own — one contextual toast unlocks the feature for the
  // 80% who'd never discover it via trial-and-error.
  useEffect(() => {
    if (!drawerOpen || !user) return;
    try {
      const tipSeen = localStorage.getItem("armk9.drawer.tipSeen.v1") === "1";
      if (tipSeen) return;
      const currentPins = loadPins();
      if (currentPins.length > 0) {
        // Already pinned something — they figured it out, no tip needed.
        localStorage.setItem("armk9.drawer.tipSeen.v1", "1");
        return;
      }
      const opens = (parseInt(localStorage.getItem("armk9.drawer.openCount.v1") || "0", 10) || 0) + 1;
      localStorage.setItem("armk9.drawer.openCount.v1", String(opens));
      if (opens === 3) {
        // Fire after a short delay so the drawer-open animation finishes
        // and the toast doesn't compete with the slide-in for attention.
        setTimeout(() => {
          toast("Tip · long-press any link to pin it to the top", {
            duration: 5000,
            description: "Your most-used pages always one tap away.",
            icon: <PushPin size={16} weight="fill" className="text-gold" />,
          });
          localStorage.setItem("armk9.drawer.tipSeen.v1", "1");
        }, 700);
      }
    } catch { /* localStorage unavailable in some embedded contexts */ }
  }, [drawerOpen, user]);

  // Global keyboard shortcut: ⌘K / Ctrl+K and "/" open the search dialog.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toUpperCase();
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "/" && !inField) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Resolve user's nav context (primary persona + available personas)
  // so the mobile drawer can hide items that don't apply to this user.
  // Same endpoint MobileBottomNav uses — keeps the two surfaces in sync.
  const [navCtx, setNavCtx] = useState(null);
  // User's pinned nav-item paths. Long-press any drawer link (mobile)
  // or click the push-pin (desktop Explore dropdown) to add/remove.
  const { pins, pinnedSet, togglePin } = useDrawerPins();
  useEffect(() => {
    if (!user) { setNavCtx(null); return; }
    let cancelled = false;
    api.get("/me/nav-context", { silent: true })
      .then((r) => { if (!cancelled) setNavCtx(r.data || null); })
      .catch(() => {
        if (!cancelled) setNavCtx({ primary: "breeder", available: ["breeder"], is_admin: false });
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const isAdmin = user?.role === "admin";
  // Compute which personas this user "has". Admins implicitly have access
  // to everything (so the drawer never hides items from them, even when
  // they're persona-switching to view as a buyer/breeder for QA).
  const userPersonas = React.useMemo(() => {
    if (isAdmin) return null; // null = no filtering (admin sees all)
    if (!navCtx) return null; // not loaded yet — show all to avoid flicker
    return new Set(navCtx.available || [navCtx.primary || "breeder"]);
  }, [isAdmin, navCtx]);

  // Filter rule: hide an item if it has `personas` AND none match this
  // user's available personas; hide `adminOnly` items from non-admins.
  const itemApplies = (it) => {
    if (it.adminOnly && !isAdmin) return false;
    if (!it.personas || userPersonas === null) return true;
    return it.personas.some((p) => userPersonas.has(p));
  };
  const items = navItems.filter(itemApplies);
  // Items collapsed into the "Tools" dropdown on desktop (still in mobile drawer).
  // Includes ops/finance pages plus secondary entries that crowded the top bar.
  const TOOLS_PATHS = new Set([
    "/payouts", "/escrow/my", "/breeder/analytics", "/import/dogs", "/me/referral", "/me/referrals",
    "/share-kennel", "/contracts", "/messages", "/offers/my", "/feed", "/breeding", "/transfers", "/breeds", "/events",
  ]);
  const toolsItems = navItems.filter((it) => TOOLS_PATHS.has(it.to));
  // Mobile drawer: admin items in their own labeled group. The only
  // admin entry whose label collides with a user-facing nav item is
  // "Events", so we relabel it for the drawer.
  const drawerAdminItems = isAdmin
    ? adminItems.map((it) =>
        it.to === "/admin/events" ? { ...it, label: "Manage events" } : it
      )
    : [];
  // Desktop nav is intentionally a SHORT list — only the highest-
  // frequency destinations. Everything else (Tools, Admin sub-pages,
  // Services, Features, Billing, Inbox, etc.) lives in the hamburger
  // drawer which is ALWAYS visible.
  //
  // Admins get an admin-forward mix: My Dogs / Dog Log / My Litters
  // are dropped because admin staff don't keep personal dogs on the
  // platform account. We surface Admin → /admin and Approvals →
  // /admin/approvals so the day-to-day work is one tap away without
  // having to open the right-side ADMIN CONSOLE pill every time.
  let desktopNavItems;
  if (user?.role === "admin") {
    desktopNavItems = [
      // Admins skip /dashboard entirely — it's a kennel/buyer page
      // (planned breedings, find-a-dog, payouts ready). Their
      // home base is /admin (operations summary), reachable via the
      // logo click and as the first nav item.
      { to: "/admin", label: "Admin", icon: ShieldStar },
      {
        to: "/admin/approvals",
        label: "Approvals",
        icon: ShieldStar,
        approvalsBadge: true,
      },
      navItems.find((it) => it.to === "/shop"),
      navItems.find((it) => it.to === "/marketplace"),
    ].filter(Boolean);
  } else {
    // `/shop` (Shop everything — unified customer-side hub) sits at
    // the FRONT of the whitelist so buyers landing on the dashboard
    // immediately see a pill that opens every customer surface in one
    // click, rather than digging through the Explore dropdown.
    const WHITELIST = new Set([
      "/dashboard", "/shop", "/marketplace", "/dogs", "/dog-log", "/litters",
    ]);
    // Preserve the WHITELIST iteration order rather than `navItems`
    // order so /shop renders second (after /dashboard) and ahead of
    // /marketplace — the most prominent placement we can give it
    // without a dedicated gold pill.
    const byTo = new Map(navItems.map((it) => [it.to, it]));
    desktopNavItems = ["/dashboard", "/shop", "/marketplace", "/dogs", "/dog-log", "/litters"]
      .map((to) => byTo.get(to))
      .filter(Boolean);
  }
  const adminBadgeCount = pendingTransfers + pendingPedigree + pendingReports;

  // Product tour sunset 2026-02-20 — replaced by the per-persona
  // OnboardingChecklist on the dashboard, which grades steps from
  // real data signals instead of pretending each user is identical.
  // The hook is kept for /help and /settings replay paths, but no
  // longer auto-mounts.
  // useProductTour();

  return (
    <div className="min-h-screen md:bg-[#FAFAFA] text-ink font-manrope">
      {/* WCAG 2.4.1 — Skip-to-content link. Visually hidden until a
          keyboard user tabs into it; styles live in index.css. */}
      <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">
        Skip to main content
      </a>
      <ImpersonationBanner />
      <VenueAckBanner />
      <LocationGate />
      <header
        className="sticky top-0 z-50 bg-white/80 md:bg-white/80 backdrop-blur-xl border-b border-transparent relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:bg-gold-hairline before:absolute before:inset-0 before:bg-gradient-to-b before:from-[#FBF7EC]/85 before:to-transparent md:before:hidden before:-z-10"
        data-testid="app-header"
      >
        <div className="w-full px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 min-w-0">
            {/* Hamburger — ALWAYS visible. Gives users a reliable
                discovery surface no matter how many pages we add. */}
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-nav-trigger"
                  aria-label="Open navigation"
                  className="p-2.5 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-ink hover:bg-[#0E0E0E]/5 transition-colors"
                >
                  <MenuIcon size={22} weight="bold" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[82vw] max-w-sm p-0 flex flex-col"
                data-testid="mobile-nav-drawer"
              >
                <VisuallyHidden>
                  <SheetTitle>Navigation</SheetTitle>
                  <SheetDescription>Primary navigation links for the registry</SheetDescription>
                </VisuallyHidden>
                <div className="flex items-center gap-3 px-5 h-16 border-b border-[#E5E5E5]/70">
                  <img
                    src="/logo-256.png"
                    alt=""
                    className="h-9 w-9 rounded-full object-contain flex-shrink-0"
                  />
                  <p className="font-spectral text-sm leading-tight text-ink">
                    Armstrong K9<br />Registry
                  </p>
                </div>
                {user && (
                  <div className="px-5 py-3 border-b border-[#E5E5E5]/70 bg-[#FBF7EC]/50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">
                          Signed in as
                        </p>
                        <p className="text-sm text-ink truncate font-semibold" data-testid="drawer-user-name">
                          {user.name}
                        </p>
                      </div>
                      {isAdmin && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#0E0E0E] text-gold text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 flex-shrink-0">
                          <ShieldStar size={11} weight="fill" />
                          Admin
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="drawer-mode-toggle">
                        <NavLink
                          to="/admin/users-ops"
                          end={false}
                          data-testid="drawer-mode-users"
                          className={() =>
                            `flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${
                              location.pathname.startsWith("/admin/users") ||
                              location.pathname.startsWith("/admin/personas") ||
                              location.pathname.startsWith("/admin/chat-reports")
                                ? "bg-[#0E0E0E] text-white"
                                : "border border-[#0E0E0E]/15 text-[#0E0E0E] hover:bg-[#0E0E0E]/5"
                            }`
                          }
                        >
                          <UsersThree size={13} weight="fill" />
                          Users
                        </NavLink>
                        <NavLink
                          to="/admin"
                          end
                          data-testid="drawer-mode-operations"
                          className={({ isActive }) =>
                            `flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${
                              isActive
                                ? "bg-[#C9A227] text-[#0E0E0E]"
                                : "border border-[#C9A227]/40 text-[#8A6A00] hover:bg-[#C9A227]/10"
                            }`
                          }
                        >
                          <Pulse size={13} weight="fill" />
                          Operations
                        </NavLink>
                        <NavLink
                          to="/admin/console"
                          data-testid="drawer-mode-command"
                          className={({ isActive }) =>
                            `flex flex-col items-center justify-center gap-0.5 h-12 rounded-lg text-[10px] font-bold uppercase tracking-[0.1em] transition-colors ${
                              isActive
                                ? "bg-[#0E0E0E] text-gold"
                                : "border border-[#0E0E0E]/15 text-[#0E0E0E] hover:bg-[#0E0E0E]/5"
                            }`
                          }
                        >
                          <ShieldStar size={13} weight="fill" />
                          Command
                        </NavLink>
                      </div>
                    )}
                    {isAdmin && adminBadgeCount > 0 && (
                      <NavLink
                        to="/admin"
                        data-testid="drawer-admin-alert"
                        className="mt-3 flex items-center gap-2 rounded-lg bg-[#FFF8E5] border border-[#C9A227]/50 px-3 py-2 hover:bg-[#FBF1D0] transition-colors"
                      >
                        <ShieldStar size={14} weight="fill" className="text-gold flex-shrink-0" />
                        <span className="text-[12px] text-[#8A6A00] font-semibold flex-1">
                          {adminBadgeCount} item{adminBadgeCount === 1 ? "" : "s"} need attention
                        </span>
                      </NavLink>
                    )}
                  </div>
                )}
                <nav className="flex-1 overflow-y-auto py-2">
                  {drawerAdminItems.length > 0 && (
                    <>
                      <div className="px-5 pt-3 pb-1 flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold flex items-center gap-1.5">
                          <ShieldStar size={11} weight="duotone" /> Admin console
                        </span>
                        <NavLink
                          to="/admin"
                          data-testid="drawer-admin-console-link"
                          className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6B6B6B] hover:text-ink"
                        >
                          Overview →
                        </NavLink>
                      </div>
                      {drawerAdminItems.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          data-testid={`mnav-${it.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                              isActive
                                ? "bg-[#FBF7EC] text-[#8A6A00] font-semibold"
                                : "text-[#3F3F3F] hover:bg-[#FAFAFA]"
                            }`
                          }
                        >
                          <it.icon size={17} weight="regular" className="flex-shrink-0 text-[#6B6B6B]" />
                          <span className="flex-1">{it.label}</span>
                          {it.transferBadge && pendingTransfers > 0 && (
                            <span className="inline-flex items-center bg-[#C9A227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                              {pendingTransfers}
                            </span>
                          )}
                          {it.pedigreeBadge && pendingPedigree > 0 && (
                            <span className="inline-flex items-center bg-[#C9A227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                              {pendingPedigree}
                            </span>
                          )}
                          {it.approvalsBadge && adminBadgeCount > 0 && (
                            <span className="inline-flex items-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full px-2 py-0.5" data-testid="mnav-approvals-badge">
                              {adminBadgeCount}
                            </span>
                          )}
                        </NavLink>
                      ))}
                      <div className="border-t border-[#E5E5E5]/70 mt-3" />
                    </>
                  )}
                  {/* Pinned section — appears above all subgroups when the
                      user has long-pressed at least one nav item. Pinned
                      items are always visible even when persona filtering
                      would normally hide them, because the user explicitly
                      chose to keep them at hand. */}
                  {(() => {
                    const pinnedItems = pins
                      .map((to) => navItems.find((n) => n.to === to))
                      .filter(Boolean);
                    if (pinnedItems.length === 0) return null;
                    return (
                      <div data-testid="mnav-pinned-section">
                        <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-gold flex items-center gap-1.5">
                          <PushPin size={11} weight="fill" /> Pinned
                        </div>
                        {pinnedItems.map((it) => (
                          <PinnableNavLink
                            key={`pin-${it.to}`}
                            it={it}
                            isPinned={true}
                            onTogglePin={togglePin}
                            unread={unread}
                            chatUnread={chatUnread}
                          />
                        ))}
                        <div className="border-t border-[#E5E5E5]/70 mt-2" />
                      </div>
                    );
                  })()}
                  <DrawerGroup
                    label={NAV_GROUPS.KENNEL}
                    rows={items.filter((i) => i.group === NAV_GROUPS.KENNEL)}
                    subgroupOrder={KENNEL_SUBGROUP_ORDER}
                    defaultOpenSubgroups={[
                      KENNEL_SUBGROUPS.DOGS,
                      KENNEL_SUBGROUPS.SELLING,
                      KENNEL_SUBGROUPS.MONEY,
                    ]}
                    pinnedSet={pinnedSet}
                    onTogglePin={togglePin}
                    unread={unread}
                  />
                  <DrawerGroup
                    label={NAV_GROUPS.COMMUNITY}
                    rows={items.filter((i) => i.group === NAV_GROUPS.COMMUNITY)}
                    subgroupOrder={COMMUNITY_SUBGROUP_ORDER}
                    defaultOpenSubgroups={[
                      COMMUNITY_SUBGROUPS.BROWSE,
                      COMMUNITY_SUBGROUPS.CONNECT,
                    ]}
                    pinnedSet={pinnedSet}
                    onTogglePin={togglePin}
                    unread={unread}
                    chatUnread={chatUnread}
                  />
                  <div className="border-t border-[#E5E5E5]/70 my-2" />
                  <NavLink
                    to="/settings"
                    data-testid="mnav-settings"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                        isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E] font-semibold" : "text-[#3F3F3F] hover:bg-[#FAFAFA]"
                      }`
                    }
                  >
                    <Gear size={17} className="text-[#6B6B6B]" />
                    Settings
                  </NavLink>
                  <NavLink
                    to="/help"
                    data-testid="mnav-help"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                        isActive ? "bg-[#0E0E0E]/5 text-[#0E0E0E] font-semibold" : "text-[#3F3F3F] hover:bg-[#FAFAFA]"
                      }`
                    }
                  >
                    <Question size={17} className="text-[#6B6B6B]" />
                    Help &amp; guide
                  </NavLink>
                  {user?.role !== "admin" && (
                    <NavLink
                      to="/inbox?compose=1"
                      data-testid="mnav-contact-admin"
                      className="flex items-center gap-3 px-5 py-2.5 text-sm text-[#3F3F3F] hover:bg-[#FAFAFA] transition-colors"
                    >
                      <PaperPlaneTilt size={17} className="text-[#6B6B6B]" />
                      Contact admin
                    </NavLink>
                  )}
                </nav>
                <div className="border-t border-[#E5E5E5]/70 p-4">
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full rounded-full border-[#E5E5E5]"
                    data-testid="drawer-logout-btn"
                  >
                    <SignOut size={16} className="mr-2" />
                    Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <NavLink to={user?.role === "admin" ? "/admin" : "/dashboard"} className="flex items-center gap-2 min-w-0 flex-shrink-0" data-testid="brand-link">
              <img
                src="/logo-256.png"
                alt="Armstrong K9 Registry"
                className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 rounded-full object-contain"
              />
              <span className="font-spectral text-sm tracking-tight leading-tight whitespace-nowrap hidden md:inline text-ink">
                Armstrong K9 Registry
              </span>
            </NavLink>
            {/* Mobile-only Explore pill — surfaces the 6–8 most-tapped
                destinations behind a bottom sheet so users don't have
                to dig through the hamburger drawer for Marketplace,
                Breeders, Events, etc. */}
            <MobileExploreDrawer user={user} navCtx={navCtx} />
            <nav className={`hidden ${isNativeApp() ? "" : "xl:flex"} items-center gap-0.5 min-w-0`}>
              {desktopNavItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[13px] whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-[#0E0E0E]/10 text-[#0E0E0E] font-semibold"
                        : "text-[#6B6B6B] hover:bg-[#FAFAFA] hover:text-[#0E0E0E]"
                    }`
                  }
                >
                  <it.icon size={15} weight="regular" className="hidden 2xl:inline" />
                  {it.label}
                  {it.badge && unread > 0 && (
                    <span className="inline-flex items-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="nav-inbox-badge">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                  {it.approvalsBadge && adminBadgeCount > 0 && (
                    <span className="inline-flex items-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="nav-approvals-badge">
                      {adminBadgeCount > 99 ? "99+" : adminBadgeCount}
                    </span>
                  )}
                </NavLink>
              ))}
              {/* "Explore" dropdown — single nav slot holding the
                  community pages (Pawprints, Breeds, Find a Mate, Events)
                  so they're one click away from the top bar without
                  re-introducing the right-side overlap with the Admin
                  Console pill. */}
              <ExploreNavDropdown navCtx={navCtx} />
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <AdminModeSwitcher />
            {/* Search — full faux-input pill on 2xl+, icon-only below */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="search-trigger"
              title="Search (⌘K)"
              className="hidden min-[1750px]:inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#E5E5E5] bg-[#FAFAFA] text-[#6B6B6B] hover:border-[#0E0E0E]/40 hover:text-ink transition-colors text-sm w-[180px]"
            >
              <MagnifyingGlass size={14} weight="bold" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="inline-flex items-center rounded border border-[#E5E5E5] bg-white px-1.5 py-0.5 text-[10px] font-mono">
                ⌘K
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              data-testid="search-trigger-mobile"
              title="Search"
              className="min-[1750px]:hidden p-2.5 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-full border border-[#E5E5E5] text-[#6B6B6B] hover:text-ink hover:border-[#0E0E0E]/40 transition-colors"
            >
              <MagnifyingGlass size={16} weight="bold" />
            </button>
            {/* Notifications bell — sits left of inbox so the
                "go-check" loops (bid invites, inquiries, watchlist
                alerts, escrow updates) collapse into one drawer. */}
            <div className="hidden sm:inline-flex p-0">
              <NotificationsBell user={user} />
            </div>
            {/* Calendar icon — quick reach to /calendar. Small surface, big
                discoverability win for a feature that benefits every persona. */}
            <NavLink
              to="/calendar"
              title="My calendar"
              data-testid="nav-calendar-icon"
              className={({ isActive }) =>
                `hidden sm:inline-flex relative p-2 rounded-full border transition-colors ${
                  isActive
                    ? "border-[#0E0E0E] text-[#0E0E0E] bg-[#0E0E0E]/5"
                    : "border-[#E5E5E5] text-[#6B6B6B] hover:text-[#0E0E0E] hover:border-[#0E0E0E]/40"
                }`
              }
            >
              <Calendar size={16} weight="duotone" />
            </NavLink>
            {/* Inbox icon — always visible (replaces text nav entry) */}
            <NavLink
              to="/inbox"
              title="Inbox"
              data-testid="nav-inbox-icon"
              className={({ isActive }) =>
                `relative p-2 rounded-full border transition-colors ${
                  isActive
                    ? "border-[#0E0E0E] text-[#0E0E0E] bg-[#0E0E0E]/5"
                    : "border-[#E5E5E5] text-[#6B6B6B] hover:text-[#0E0E0E] hover:border-[#0E0E0E]/40"
                }`
              }
            >
              <Envelope size={16} weight="duotone" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center bg-[#C1272D] text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1" data-testid="nav-inbox-badge">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
            {/* Billing icon — desktop-only (mobile users open it from the drawer) */}
            <NavLink
              to="/billing"
              title={isNativeApp() ? "My Account" : "Billing"}
              data-testid="nav-billing-icon"
              className={({ isActive }) =>
                `hidden sm:inline-flex p-2 rounded-full border transition-colors ${
                  isActive
                    ? "border-[#0E0E0E] text-[#0E0E0E] bg-[#0E0E0E]/5"
                    : "border-[#E5E5E5] text-[#6B6B6B] hover:text-[#0E0E0E] hover:border-[#0E0E0E]/40"
                }`
              }
            >
              {isNativeApp() ? <UserCircle size={16} weight="duotone" /> : <CurrencyDollar size={16} weight="duotone" />}
            </NavLink>
            {user?.role !== "admin" && (
              <NavLink
                to="/inbox?compose=1"
                title="Contact admin"
                data-testid="nav-contact-admin"
                className="hidden sm:inline-flex p-2 rounded-full border border-[#E5E5E5] text-[#6B6B6B] hover:text-ink hover:border-[#0E0E0E]/40 transition-colors"
              >
                <PaperPlaneTilt size={16} weight="duotone" />
              </NavLink>
            )}
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `hidden sm:inline-flex p-2 rounded-full border transition-colors ${
                  isActive
                    ? "border-[#0E0E0E] text-[#0E0E0E] bg-[#0E0E0E]/5"
                    : "border-[#E5E5E5] text-[#6B6B6B] hover:text-[#0E0E0E] hover:border-[#0E0E0E]/40"
                }`
              }
              title="Settings"
              data-testid="nav-settings"
            >
              <Gear size={16} />
            </NavLink>
            <NavLink
              to="/help"
              className={({ isActive }) =>
                `hidden sm:inline-flex p-2 rounded-full border transition-colors ${
                  isActive
                    ? "border-[#0E0E0E] text-[#0E0E0E] bg-[#0E0E0E]/5"
                    : "border-[#E5E5E5] text-[#6B6B6B] hover:text-[#0E0E0E] hover:border-[#0E0E0E]/40"
                }`
              }
              title="Help & getting started"
              data-testid="nav-help"
            >
              <Question size={16} weight="duotone" />
            </NavLink>
            <button
              onClick={handleLogout}
              type="button"
              className="hidden sm:inline-flex p-2 rounded-full border border-[#E5E5E5] text-[#6B6B6B] hover:text-crimson hover:border-[#C1272D]/40 transition-colors"
              data-testid="logout-btn"
              title="Sign out"
            >
              <SignOut size={16} />
              <span className="sr-only">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main id="main-content" className="max-w-7xl mx-auto p-4 sm:p-6 md:p-10">
        {/* Auto-rendered "Back to admin console" pill on every admin
            sub-page (URL starts with /admin/ and isn't /admin or
            /admin/console itself). Single-source so we don't have to
            thread a prop through 20+ admin pages.

            Click behaviour: prefer browser-history back so a deep card
            click on `/admin/console` → sub-page → Back returns the
            admin to the Command Center, not to the Operations tab.
            First-visit / refresh / deep-link fallback is
            /admin/console (the Command Center landing) — NOT /admin,
            because /admin is the Operations route and silently
            redirecting there is what the user complained about. */}
        {location.pathname.startsWith("/admin/") &&
        location.pathname !== "/admin/console" ? (
          <a
            href="/admin/console"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-3 py-2 min-h-[40px] text-[12px] font-bold text-ink hover:border-[#0E0E0E] hover:bg-[#FAFAFA] transition-colors mb-5"
            data-testid="appshell-back-to-admin"
            onClick={(e) => {
              e.preventDefault();
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/admin/console");
              }
            }}
          >
            ← Back
          </a>
        ) : (
          /* Universal "Back" pill everywhere else inside the authed
             shell. Falls back to /dashboard if there's no history. */
          <BackButton fallback="/dashboard" />
        )}
        <Tax1099KBanner />
        {children}
      </main>
      {isNativeApp() ? (
        <footer className="mt-12 pb-6 pt-4 border-t border-[#EAEAEA]">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-4 text-[12px] text-[#9A9A9A]">
            <NavLink to="/terms" className="hover:text-ink" data-testid="native-footer-terms">Terms</NavLink>
            <span>·</span>
            <NavLink to="/privacy" className="hover:text-ink" data-testid="native-footer-privacy">Privacy</NavLink>
            <span>·</span>
            <NavLink to="/help" className="hover:text-ink" data-testid="native-footer-help">Help</NavLink>
          </div>
        </footer>
      ) : (
      <footer className="relative bg-white/50 mt-12 before:absolute before:left-0 before:right-0 before:top-0 before:h-px before:bg-gold-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <p
            className="text-[11px] leading-relaxed text-[#6B6B6B] italic max-w-3xl mb-8"
            data-testid="footer-venue-disclaimer"
          >
            Armstrong K9 Registry is a <strong>venue</strong> connecting
            buyers, sellers, and breeders for purebred and exotic dogs. The Registry
            is not the seller and does not warrant the health, lineage, temperament,
            or fitness of any animal. Sellers are independent merchants of record for
            their own sales and taxes. See <NavLink to="/seller-standards" className="underline hover:text-ink">Seller standards</NavLink> and <NavLink to="/terms" className="underline hover:text-ink">Terms of Service</NavLink>.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson mb-3">Product</p>
              <ul className="space-y-2 text-[13px] text-[#6B6B6B]">
                <li><NavLink to="/armstrong" className="hover:text-ink font-semibold text-ink" data-testid="shell-footer-armstrong">Armstrong Shop · dog food &amp; merch</NavLink></li>
                <li><NavLink to="/shop" className="hover:text-ink">Shop everything</NavLink></li>
                <li><NavLink to="/marketplace" className="hover:text-ink">Dog Marketplace</NavLink></li>
                <li><NavLink to="/find-pros" className="hover:text-ink">Find a service pro</NavLink></li>
                <li><NavLink to="/breeding" className="hover:text-ink">Breeding directory</NavLink></li>
                <li><NavLink to="/features" className="hover:text-ink" data-testid="shell-footer-features">Features</NavLink></li>
                <li><NavLink to="/billing" className="hover:text-ink">Plans &amp; pricing</NavLink></li>
                <li><NavLink to="/events" className="hover:text-ink">Events</NavLink></li>
                <li><NavLink to="/install" className="hover:text-ink font-bold" data-testid="shell-footer-install">Get the app</NavLink></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-3">Trust &amp; safety</p>
              <ul className="space-y-2 text-[13px] text-[#6B6B6B]">
                <li><NavLink to="/seller-standards" className="hover:text-ink" data-testid="footer-seller-standards">Seller standards</NavLink></li>
                <li><NavLink to="/buyer-rights" className="hover:text-ink" data-testid="footer-buyer-rights">Buyer's bill of rights</NavLink></li>
                <li><NavLink to="/ethics" className="hover:text-ink">Ethical considerations</NavLink></li>
                <li><NavLink to="/security" className="hover:text-ink">Security measures</NavLink></li>
                <li><NavLink to="/compliance" className="hover:text-ink">Compliance</NavLink></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F8043] mb-3">Legal</p>
              <ul className="space-y-2 text-[13px] text-[#6B6B6B]">
                <li><NavLink to="/terms" className="hover:text-ink" data-testid="shell-footer-terms">Terms of Service</NavLink></li>
                <li><NavLink to="/privacy" className="hover:text-ink" data-testid="shell-footer-privacy">Privacy Policy</NavLink></li>
                <li><NavLink to="/dmca" className="hover:text-ink" data-testid="shell-footer-dmca">DMCA notice</NavLink></li>
                <li><NavLink to="/refund" className="hover:text-ink">Refund Policy</NavLink></li>
                <li><NavLink to="/sub-processors" className="hover:text-ink">Sub-processors</NavLink></li>
                <li><NavLink to="/accessibility" className="hover:text-ink">Accessibility</NavLink></li>
                <li><NavLink to="/consent" className="hover:text-ink">User consent</NavLink></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3F4A8A] mb-3">Company</p>
              <ul className="space-y-2 text-[13px] text-[#6B6B6B]">
                <li><NavLink to="/about" className="hover:text-ink" data-testid="shell-footer-about">About</NavLink></li>
                <li><NavLink to="/contact" className="hover:text-ink">Contact</NavLink></li>
                <li><NavLink to="/status" className="hover:text-ink">System status</NavLink></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] mt-10 pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-[#6B6B6B]">
            <p>
              © {new Date().getFullYear()} Armstrong K9 Registry
              <span className="hidden sm:inline"> · Purebreds &amp; exotic breeds</span>
            </p>
            <p className="font-mono text-[11px]">hello@armstrongk9registry.com</p>
          </div>
        </div>
        {/* Spacer so the floating "Made with Emergent" badge doesn't occlude footer content */}
        <div className="h-24 md:h-16" aria-hidden="true" />
      </footer>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <MobileBottomNav user={user} unread={unread} onOpenSearch={() => setSearchOpen(true)} />
      <GoogleSheetsInstructionsDialog />
      <AppInstallAndPushPrompt user={user} />
    </div>
  );
}


// Drawer group renderer — section header + nav links. Kept inline (not
// exported) because it's used only by the mobile drawer in AppShell.
//
// All subgroups are collapsible <details> expanders. Each remembers
// its open/closed state per-user via localStorage so the drawer "learns"
// which subgroups the user actually uses. Default-open list is passed
// from the parent — first-time visitors see the most-used subgroups
// expanded; rarely-used subgroups stay collapsed unless explicitly
// expanded (and that choice then persists).
const SUBGROUP_STATE_KEY = "armk9.drawer.subgroups.v1";
function loadSubgroupState() {
  try {
    const raw = localStorage.getItem(SUBGROUP_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveSubgroupState(key, isOpen) {
  try {
    const state = loadSubgroupState();
    state[key] = isOpen;
    localStorage.setItem(SUBGROUP_STATE_KEY, JSON.stringify(state));
  } catch { /* no-op */ }
}

// Drawer pin storage — user long-presses any nav item (mobile) or
// clicks the push-pin button (desktop Explore dropdown) to pin it to
// the top. Pinned items always show regardless of persona filtering
// or subgroup collapse state. State is shared across mobile + desktop
// surfaces so a pin made on phone shows up on laptop.
const PIN_STATE_KEY = "armk9.drawer.pins.v1";
function loadPins() {
  try {
    const raw = localStorage.getItem(PIN_STATE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function savePins(arr) {
  try {
    localStorage.setItem(PIN_STATE_KEY, JSON.stringify(arr));
  } catch { /* no-op */ }
}

/**
 * Shared hook for pin state. Used by both the mobile hamburger drawer
 * (AppShell) and the desktop Explore dropdown (ExploreNavDropdown).
 * Listens to `storage` events so toggling on one tab updates the other.
 */
function useDrawerPins() {
  const [pins, setPins] = React.useState(() => loadPins());
  // Sync across browser tabs — pin on tab A, see it on tab B.
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PIN_STATE_KEY) setPins(loadPins());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const pinnedSet = React.useMemo(() => new Set(pins), [pins]);
  const togglePin = React.useCallback((to, label) => {
    setPins((curr) => {
      const isPinned = curr.includes(to);
      const next = isPinned
        ? curr.filter((p) => p !== to)
        : [...curr, to];
      savePins(next);
      toast.success(
        isPinned ? `Unpinned "${label}"` : `Pinned "${label}" to top`,
        { duration: 2000 }
      );
      return next;
    });
  }, []);
  return { pins, pinnedSet, togglePin };
}

/**
 * NavLink + long-press handler. Long-press (~500ms) toggles whether
 * this item is pinned to the top of the drawer. A subtle scale + ring
 * during press provides feedback; a toast confirms the result.
 */
function PinnableNavLink({ it, isPinned, onTogglePin, unread, chatUnread = 0 }) {
  const timerRef = React.useRef(null);
  const triggeredRef = React.useRef(false);
  const [pressing, setPressing] = React.useState(false);

  const start = (e) => {
    // Only trigger on primary button / touch.
    if (e.button !== undefined && e.button !== 0) return;
    triggeredRef.current = false;
    setPressing(true);
    timerRef.current = setTimeout(() => {
      triggeredRef.current = true;
      setPressing(false);
      onTogglePin(it.to, it.label);
      // Light haptic where supported.
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try { navigator.vibrate(10); } catch { /* no-op */ }
      }
    }, 500);
  };
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPressing(false);
  };
  return (
    <NavLink
      to={it.to}
      data-testid={`mnav-${it.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      // If the long-press fired, swallow the synthetic click so we
      // don't navigate to the page the user was trying to pin.
      onClickCapture={(e) => {
        if (triggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          triggeredRef.current = false;
        }
      }}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2.5 text-sm transition-all select-none ${
          pressing ? "scale-[0.985] bg-[#FBF7EC]" : ""
        } ${
          isActive
            ? "bg-[#0E0E0E]/5 text-[#0E0E0E] font-semibold"
            : "text-[#3F3F3F] hover:bg-[#FAFAFA]"
        }`
      }
    >
      <it.icon size={17} weight="regular" className="flex-shrink-0 text-[#6B6B6B]" />
      <span className="flex-1">{it.label}</span>
      {isPinned && (
        <PushPin size={12} weight="fill" className="text-[#C9A227] flex-shrink-0" data-testid={`mnav-pin-indicator`} />
      )}
      {it.badge && unread > 0 && (
        <span className="inline-flex items-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full px-2 py-0.5">
          {unread}
        </span>
      )}
      {it.chatBadge && chatUnread > 0 && (
        <span className="inline-flex items-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full px-2 py-0.5" data-testid="nav-chat-badge">
          {chatUnread > 99 ? "99+" : chatUnread}
        </span>
      )}
    </NavLink>
  );
}

function DrawerGroup({ label, rows, subgroupOrder, unread, chatUnread = 0, defaultOpenSubgroups, pinnedSet, onTogglePin }) {
  const defaultOpenSet = new Set(defaultOpenSubgroups || []);
  // Load persisted state once per mount. Subsequent opens re-render
  // because the user toggled (and we already wrote the new value).
  // Hook must be called before any early return — `rows` may be empty.
  const persisted = React.useMemo(() => loadSubgroupState(), []);

  if (!rows || rows.length === 0) return null;

  // Build subgroup buckets in the stable order provided by the parent.
  // Items without a `subgroup` go into the `null` bucket which renders
  // first (the "pinned / most-used" zone at the top of each group).
  const buckets = new Map();
  (subgroupOrder || [null]).forEach((key) => buckets.set(key, []));
  rows.forEach((row) => {
    const key = row.subgroup ?? null;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  });

  return (
    <>
      <div className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
        {label}
      </div>
      {[...buckets.entries()].map(([key, items]) => {
        if (items.length === 0) return null;
        // null bucket (pinned, most-used) is always rendered flat —
        // never collapsed. Subgroup buckets are <details> expanders.
        const isCollapsible = !!key;
        const renderItems = () => items.map((it) => (
          <PinnableNavLink
            key={it.to}
            it={it}
            isPinned={pinnedSet?.has(it.to) || false}
            onTogglePin={onTogglePin}
            unread={unread}
            chatUnread={chatUnread}
          />
        ));
        if (isCollapsible) {
          // Persisted user choice wins; otherwise fall back to the
          // default-open set; otherwise default to closed. This lets
          // power users hide subgroups they never touch.
          const storageKey = `${label}::${key}`;
          const persistedOpen = persisted[storageKey];
          const initialOpen = typeof persistedOpen === "boolean"
            ? persistedOpen
            : defaultOpenSet.has(key);
          return (
            <details
              key={key}
              className="group"
              data-testid={`mnav-collapsible-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              open={initialOpen || undefined}
              onToggle={(e) => saveSubgroupState(storageKey, e.currentTarget.open)}
            >
              <summary
                className="px-5 pt-2 pb-1 text-[9.5px] uppercase tracking-[0.18em] font-semibold text-[#9A9A9A] cursor-pointer list-none flex items-center gap-1.5 hover:text-[#3F3F3F] select-none"
              >
                {key}
                <CaretDown size={10} weight="bold" className="transition-transform group-open:rotate-180" />
                <span className="ml-auto text-[9.5px] text-[#BBBBBB] font-normal">{items.length}</span>
              </summary>
              <div className="pb-1">{renderItems()}</div>
            </details>
          );
        }
        return (
          <React.Fragment key={key ?? "__pinned__"}>
            {renderItems()}
          </React.Fragment>
        );
      })}
    </>
  );
}


// Desktop "Explore ▾" dropdown — surfaces the community pages
// at the top of the page in a single nav slot.
//
// Items are now grouped into BROWSE (the marketplace / discovery
// surfaces — highest-traffic), TOOLS (utilities the breeder reaches
// for daily), and READ (editorial). Within each group, order reflects
// observed click-through. Find a Mate stays the featured first item
// at the top of BROWSE — founder explicitly asked us to keep it
// prominent (2026-05-28).
const EXPLORE_GROUPS = [
  {
    label: "Most popular",
    items: [
      { to: "/calendar",  label: "My Calendar", icon: Calendar, blurb: "Heats, breedings, transport jobs & custom reminders in one view", featured: true },
      { to: "/find-pros", label: "Find a Service Pro", icon: GraduationCap, blurb: "Trainers, groomers, walkers & sitters — vetted providers near you", featured: true },
      { to: "/storefronts", label: "Storefronts", icon: Storefront, blurb: "Kennel storefronts — food, treats, and merch from breeders", featured: true },
    ],
  },
  {
    label: "Browse",
    items: [
      { to: "/breeding",  label: "Breeding Matchmaker", icon: HeartStraight, blurb: "Find studs & dams for your breeding program — COI, distance & health scored", featured: true },
      { to: "/find-pros?kind=field_host", label: "Zoomie Time", icon: Tree, blurb: "Rent a private field by the hour for safe, off-leash zoomies", featured: true },
      { to: "/courses",   label: "Trainer courses", icon: GraduationCap, blurb: "Pre-recorded video courses + live 1-on-1 sessions from approved trainers" },
      { to: "/catalog",   label: "All Dogs", icon: Dog, blurb: "Public photo catalog of every registered dog, grouped by breed" },
      { to: "/breeds",    label: "Breeds",      icon: Compass,       blurb: "Searchable breed catalog" },
      { to: "/events",    label: "Events",      icon: Calendar,      blurb: "Shows, meetups & seminars" },
      { to: "/transport", label: "Transport", icon: Truck, blurb: "Verified haulers · USDA-licensed · Armstrong K9 Registry-escrowed" },
    ],
  },
  {
    label: "Community",
    items: [
      { to: "/feed",      label: "The Dog's Voice", icon: ChatCircle,    blurb: "Dog-perspective social feed" },
      { to: "/journal",   label: "Journal", icon: BookOpen, blurb: "Editorial notes from the registry — verification, trust, pedigrees" },
    ],
  },
  {
    label: "About the platform",
    items: [
      { to: "/features",  label: "Platform features", icon: Sparkle, blurb: "What every persona gets at the platform level" },
      { to: "/one-pagers", label: "Persona 1-pagers (PDF)", icon: PaperPlaneTilt, blurb: "Branded 1-page overviews for each of the 6 personas — share with prospects", adminOnly: true },
    ],
  },
];

// Flat fallback used by the active-state check + unit tests that
// imported `EXPLORE_LINKS`. Keep order identical to the grouped
// rendering above so analytics line up.
const EXPLORE_LINKS = EXPLORE_GROUPS.flatMap((g) => g.items);

// "My Storefront" pinned to the bottom of the Explore dropdown for
// any signed-in user. Routes to /me/store which handles the seller-
// onboarding cascade (subscribe → Stripe Connect → claim slug) for
// users who don't yet have a store, so the link is meaningful even
// before they open one.
const SELLER_LINK = {
  to: "/me/store",
  label: "My Storefront",
  icon: Storefront,
  blurb: "Manage your products, orders, and Printify merch",
};

// Service pros get a one-tap link to their provider page — bookings,
// services, availability, profile edits — pinned in its own group so
// they never dig for it.
const PROVIDER_LINK = {
  to: "/provider/me",
  label: "My Provider page",
  icon: Wrench,
  blurb: "Bookings, services, availability & profile — manage it all",
  featured: true,
};

function ExploreNavDropdown({ navCtx }) {
  const location = useLocation();
  const { user } = useAuth();
  const { pinnedSet, togglePin } = useDrawerPins();
  const isAdmin = user?.role === "admin";
  const isProvider =
    (user?.persona || "").toLowerCase() === "service_provider" ||
    (navCtx?.available || []).includes("service_provider");
  // Filter out admin-only items (e.g., Persona 1-pagers PDF — founder
  // marketing collateral, not user-facing nav). Mirrors the mobile
  // drawer's persona filtering so the two surfaces stay in sync.
  const filterItems = (items) => items.filter((it) => !it.adminOnly || isAdmin);
  const groupsToRender = (user
    ? [
        ...(isProvider ? [{ label: "My business", items: [PROVIDER_LINK] }] : []),
        ...EXPLORE_GROUPS,
        { label: "Selling", items: [SELLER_LINK] },
      ]
    : EXPLORE_GROUPS
  )
    .map((g) => ({ ...g, items: filterItems(g.items) }))
    .filter((g) => g.items.length > 0);
  const flatLinks = groupsToRender.flatMap((g) => g.items);
  const isActive = flatLinks.some((l) => location.pathname.startsWith(l.to));
  // Build the synthetic "Pinned" group from items the user pinned (in
  // either the mobile drawer or here). Limited to links that exist in
  // EXPLORE_GROUPS so a pinned-but-unreachable item doesn't appear.
  const pinnedItems = React.useMemo(() => {
    return flatLinks.filter((l) => pinnedSet.has(l.to));
  }, [flatLinks, pinnedSet]);
  const groupsWithPinned = pinnedItems.length > 0
    ? [{ label: "Pinned", items: pinnedItems, isPinnedGroup: true }, ...groupsToRender]
    : groupsToRender;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="nav-explore-trigger"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[13px] whitespace-nowrap transition-colors ${
            isActive
              ? "bg-[#0E0E0E]/10 text-[#0E0E0E] font-semibold"
              : "text-[#6B6B6B] hover:bg-[#FAFAFA] hover:text-[#0E0E0E]"
          }`}
        >
          <Compass size={15} weight="regular" className="hidden 2xl:inline" />
          Explore
          <CaretDown size={11} weight="bold" className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-64 p-1 bg-white border border-[#EAEAEA] shadow-luxury-light rounded-xl"
        data-testid="nav-explore-menu"
      >
        {groupsWithPinned.map((group, gi) => (
          <React.Fragment key={group.label}>
            <DropdownMenuLabel
              className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1.5 flex items-center gap-1.5 ${
                group.isPinnedGroup ? "text-gold-dark" : "text-[#6B6B6B]"
              } ${gi > 0 ? "mt-1 border-t border-[#F0F0F0] pt-2" : ""}`}
            >
              {group.isPinnedGroup && <PushPin size={11} weight="fill" />}
              {group.label}
            </DropdownMenuLabel>
            {group.items.map((l) => {
              const isPinned = pinnedSet.has(l.to);
              return (
                <div
                  key={l.to}
                  className="relative group/item"
                  data-testid={`nav-explore-row-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <DropdownMenuItem
                    asChild
                    className="cursor-pointer rounded-lg focus:bg-[#FBF7EC]"
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-explore-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => {
                        // Lightweight discovery analytics — tells us which of
                        // the community pages actually get traffic from the
                        // top nav vs. the hamburger drawer.
                        track("nav_explore_clicked", {
                          destination: l.to,
                          label: l.label,
                          group: group.label,
                        });
                        // Mirror to our own MongoDB so admins can read
                        // aggregates at /admin/analytics without needing
                        // a PostHog dashboard. Fire-and-forget; never
                        // breaks the user's navigation if it fails.
                        api.post("/analytics/nav-click", {
                          surface: "explore_dropdown",
                          destination: l.to,
                          label: l.label,
                          group: group.label,
                        }, { silent: true }).catch(() => { /* no-op */ });
                      }}
                      className={({ isActive: linkActive }) =>
                        `flex items-start gap-2.5 px-2 py-2 pr-8 ${linkActive ? "bg-[#FBF7EC]" : ""} ${l.featured ? "border-l-2 border-gold" : ""}`
                      }
                    >
                      <span className={`p-1.5 rounded-md flex-shrink-0 mt-0.5 ${l.featured ? "bg-gold-gradient ring-1 ring-gold/40" : "bg-cream ring-1 ring-[#E2DAC2]"}`}>
                        <l.icon size={13} weight="duotone" className={l.featured ? "text-ink" : "text-gold"} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-ink leading-tight">{l.label}</span>
                          {l.featured && (
                            <span className="text-[8.5px] font-bold uppercase tracking-wider bg-gold-gradient text-ink rounded-full px-1.5 leading-[1.4]">
                              Top
                            </span>
                          )}
                        </span>
                        {l.blurb && (
                          <span className="block text-[11px] text-[#6B6B6B] leading-snug mt-0.5">
                            {l.blurb}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  </DropdownMenuItem>
                  {/* Pin / unpin button — visible on row hover (or always
                      if already pinned so the user can unpin without
                      having to find the hover sweet-spot). */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePin(l.to, l.label);
                    }}
                    data-testid={`nav-explore-pin-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={isPinned ? `Unpin ${l.label}` : `Pin ${l.label} to top`}
                    title={isPinned ? `Unpin ${l.label}` : `Pin ${l.label} to top`}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full transition-opacity ${
                      isPinned
                        ? "opacity-100 text-gold-dark hover:bg-[#FBF1D0]"
                        : "opacity-0 group-hover/item:opacity-100 text-[#9A9A9A] hover:bg-[#FAFAFA] hover:text-ink"
                    }`}
                  >
                    <PushPin size={12} weight={isPinned ? "fill" : "regular"} />
                  </button>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
